import React from "react";
import PageWrapper from "../components/PageWrapper";
import { motion } from "framer-motion";

export default function Home() {
  return (
    <PageWrapper>

      {/* HERO SECTION */}
      <section className="relative overflow-hidden min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white flex items-center pt-24 pb-16">

        {/* Floating Blurred Background Shapes */}
        <div className="absolute w-72 h-72 bg-white/10 blur-3xl rounded-full -top-10 -left-10 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-blue-300/10 blur-2xl rounded-full -bottom-20 right-0 animate-pulse"></div>

        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center relative z-10">
          
          {/* LEFT TEXT SECTION */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="text-5xl md:text-6xl font-extrabold leading-tight"
            >
              My School App
              <br />
              <span className="text-yellow-300 drop-shadow-lg">
                Next-Generation
              </span>
              <br />
              Academic System
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-6 text-lg opacity-90 leading-relaxed"
            >
              Powerful, secure, and cloud-driven School Management Software
              that transforms your school with AI, real-time analytics, and
              beautiful mobile-first apps for principals, teachers, and parents.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mt-8 flex gap-4"
            >
              {/* Explore Button */}
              <button className="px-6 py-3 bg-yellow-400 text-black font-bold rounded-lg shadow-lg hover:bg-yellow-300 transition hover:scale-105 hover:shadow-yellow-300/50">
                Explore Products
              </button>

              {/* Contact Button */}
              <button className="px-6 py-3 bg-white/90 text-blue-700 font-bold rounded-lg shadow-lg hover:bg-white transition hover:scale-105">
                Contact Us
              </button>
            </motion.div>
          </motion.div>

          {/* RIGHT SIDE — Animated 3D Icons */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative flex justify-center"
          >
            <div className="w-[350px] h-[350px] md:w-[450px] md:h-[450px] rounded-full bg-white/10 backdrop-blur-2xl shadow-2xl flex items-center justify-center relative border border-white/20">

              {/* Floating Icons */}
              <AnimatedIcon className="top-6 left-8" icon="📚" duration={4} />
              <AnimatedIcon className="bottom-10 right-10" icon="🎓" duration={5} />
              <AnimatedIcon className="top-20 right-24" icon="🏫" duration={3.5} />
              <AnimatedIcon className="bottom-16 left-20" icon="⚡" duration={4.5} />

            </div>
          </motion.div>

        </div>
      </section>

      {/* FEATURES SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl font-bold text-gray-800 drop-shadow-sm">
            Why Choose My School App?
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto leading-relaxed">
            A complete educational ERP connecting administrators, teachers,
            parents, and students through automation and real-time data.
          </p>

          <div className="grid md:grid-cols-3 gap-10 mt-14">
            <FeatureCard
              icon="⚡"
              title="Real-Time Sync"
              subtitle="Live data updates across the entire school"
            />
            <FeatureCard
              icon="🔒"
              title="100% Secure"
              subtitle="Encrypted cloud-based system with enterprise-level safety"
            />
            <FeatureCard
              icon="📱"
              title="Cross-Platform Apps"
              subtitle="Apps for principals, teachers, and parents"
            />
          </div>
        </div>
      </section>


{/* OUR PRODUCTS & SOLUTIONS */}
<section className="py-24 bg-gray-50">
  <div className="max-w-7xl mx-auto px-6">

    {/* Section Heading */}
    <div className="text-center mb-14">
      <h2 className="text-4xl font-extrabold text-gray-800">
        Our Products & Solutions
      </h2>
      <p className="mt-3 text-gray-600 max-w-2xl mx-auto leading-relaxed">
        A complete next-generation academic management ecosystem empowering 
        Principals, Teachers, Parents, and entire school operations.
      </p>
    </div>

    {/* Product Cards Grid */}
    <div className="grid md:grid-cols-3 gap-10">

      {/* Principal App */}
      <ProductCard
        title="Principal App"
        icon="🏫"
        gradient="from-blue-500 via-purple-500 to-indigo-600"
        points={[
          "Real-Time Analytics Dashboard",
          "Financial Oversight & Fee Tracking",
          "Attendance & Leave Management",
        ]}
      />

      {/* Parent App */}
      <ProductCard
        title="Parent App"
        icon="👨‍👩‍👧"
        gradient="from-pink-500 via-red-500 to-yellow-500"
        points={[
          "Track Attendance & Homework",
          "Instant Notifications & Alerts",
          "Multi-child Dashboard",
        ]}
      />

      {/* Teacher App */}
      <ProductCard
        title="Teacher App"
        icon="👩‍🏫"
        gradient="from-green-500 via-emerald-500 to-teal-500"
        points={[
          "AI-Assisted Lesson Planning",
          "Easy Attendance Marking",
          "Homework & Assignment Management",
        ]}
      />

    </div>

    {/* SECOND ROW */}
    <div className="grid md:grid-cols-3 gap-10 mt-12">

      {/* Branded School App */}
      <ProductCard
        title="Branded School App"
        icon="📱"
        gradient="from-indigo-500 via-blue-500 to-cyan-500"
        points={[
          "Your School App on Play Store",
          "Real-time data sync",
          "Homework & Grade System",
        ]}
      />

      {/* AI Features */}
      <ProductCard
        title="AI Powered Tools"
        icon="🤖"
        gradient="from-purple-500 via-fuchsia-500 to-pink-500"
        points={[
          "Audio to Text (Any Language)",
          "AI-driven communication",
          "Smart analytics",
        ]}
      />

      {/* ERP Solutions */}
      <ProductCard
        title="Full ERP System"
        icon="🗂️"
        gradient="from-yellow-500 via-orange-500 to-red-500"
        points={[
          "Fees, Exams, Library, Transport",
          "Hostel Management System",
          "Staff & HR Management",
        ]}
      />

    </div>

  </div>
</section>
{/* PREMIUM COLORFUL SECTION */}
<section className="py-24 bg-gradient-to-br from-[#eef4ff] via-[#f3e8ff] to-[#ffe8f0] relative overflow-hidden">

  {/* floating blur backgrounds */}
  <div className="absolute w-96 h-96 bg-purple-300/40 blur-3xl rounded-full -top-10 -left-20 animate-pulse"></div>
  <div className="absolute w-80 h-80 bg-pink-300/40 blur-2xl rounded-full bottom-10 right-0 animate-pulse"></div>

  <div className="relative z-10 max-w-7xl mx-auto px-6">

    {/* heading */}
    <div className="text-center mb-16">
      <h2 className="text-4xl font-extrabold text-gray-800 drop-shadow">
        Why Our ERP System Is Next-Level
      </h2>
      <p className="text-gray-700 mt-3 max-w-2xl mx-auto leading-relaxed">
        A modern AI-driven ecosystem designed for automation, scalability and smooth digital school operations.
      </p>
    </div>

    {/* colorful animated cards */}
    <div className="grid md:grid-cols-3 gap-10">

      <PremiumFeature
        icon="🤖"
        title="AI-Powered Automation"
        colors="from-purple-500/20 to-pink-500/20"
        text="Smarter operations with Audio-to-Text, AI analytics, and automated communication."
      />

      <PremiumFeature
        icon="📱"
        title="4 Connected Apps"
        colors="from-blue-500/20 to-cyan-500/20"
        text="Student, Parent, Teacher & Hostel apps with real-time data sync."
      />

      <PremiumFeature
        icon="🛰️"
        title="Multi-Tenant SaaS"
        colors="from-yellow-400/30 to-orange-400/20"
        text="Each school runs independently with isolated data and its own subdomain."
      />

    </div>

    <div className="grid md:grid-cols-3 gap-10 mt-12">

      <PremiumFeature
        icon="🌐"
        title="Auto School Website"
        colors="from-indigo-400/20 to-blue-400/20"
        text="Each school gets a synced dynamic website with admission form, gallery & notices."
      />

      <PremiumFeature
        icon="🔒"
        title="Enterprise Security"
        colors="from-red-400/20 to-rose-400/20"
        text="SSL, encryption, RBAC rules, backups & data compliance."
      />

      <PremiumFeature
        icon="⚡"
        title="Cloud Performance"
        colors="from-green-400/20 to-teal-400/20"
        text="Optimized APIs and distributed architecture for fast performance."
      />

    </div>

  </div>
</section>



    </PageWrapper>
  );
}
function PremiumFeature({ icon, title, text, colors }) {
  return (
    <motion.div
      whileHover={{ scale: 1.06, y: -8 }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className={`p-8 rounded-2xl shadow-xl bg-gradient-to-br ${colors} backdrop-blur-xl border border-white/40 hover:shadow-2xl transition`}
    >
      <div className="text-5xl mb-4 drop-shadow">{icon}</div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-700 leading-relaxed">{text}</p>
    </motion.div>
  );
}
function ProductCard({ title, icon, gradient, points }) {
  return (
    <motion.div
      whileHover={{ scale: 1.05, y: -5 }}
      className={`p-8 bg-white rounded-2xl shadow-xl border border-gray-200 hover:shadow-2xl transition relative overflow-hidden`}
    >
      {/* Gradient Top Strip */}
      <div
        className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${gradient}`}
      ></div>

      {/* Icon */}
      <div className="text-5xl mb-4">{icon}</div>

      {/* Title */}
      <h3 className="text-2xl font-bold text-gray-800">{title}</h3>

      {/* Points */}
      <ul className="mt-4 space-y-2 text-gray-600 font-medium">
        {points.map((p, i) => (
          <li key={i} className="flex items-start">
            <span className="text-blue-600 font-bold mr-2">•</span>
            {p}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}
// ANIMATED FLOATING ICON
function AnimatedIcon({ icon, className, duration }) {
  return (
    <motion.div
      animate={{ y: [-15, 15, -15] }}
      transition={{ duration, repeat: Infinity }}
      className={`absolute bg-white/20 p-4 text-4xl rounded-xl backdrop-blur-xl shadow-lg border border-white/30 ${className}`}
    >
      {icon}
    </motion.div>
  );
}


// FEATURE CARD
function FeatureCard({ icon, title, subtitle }) {
  return (
    <motion.div
      whileHover={{ scale: 1.05, y: -5 }}
      className="p-8 bg-white shadow-xl rounded-xl border border-gray-100 hover:shadow-2xl transition"
    >
      <div className="text-5xl">{icon}</div>
      <h3 className="text-xl font-bold mt-4">{title}</h3>
      <p className="text-gray-600 mt-2 leading-relaxed">{subtitle}</p>
    </motion.div>
  );
}